import { logger } from '../../../pbir/utils/logger';

/**
 * Properties with this decorator will be automatically unsubscribed when ngOnDestroy is called.
 * If the property value is null or undefined in ngOnDestroy, it will be ignored.
 * If the property value is not a SubscriptionLike, a warning will be issued.
 * The unsubscription occurs after the ngOnDestroy call.
 * Errors occurring as part of the unsubscribe process will not be rethrown.
 */
export function UnsubscribeOnDestroy() {
  return function (component: any, propertyName: string) {
    unsubscribeOnDestroy.addProperty(component, propertyName);
  }
}

const LOGGER = logger('UnsubscribeOnDestroy');

namespace unsubscribeOnDestroy {
  interface ITargetData {
    keys: Set<string>;
  };

  interface ITargetPrototype {
    ngOnDestroy?: Function;
  }

  interface ITarget {
    constructor: {
      prototype: ITargetPrototype,
      $$unsubscribeOnDestroyKeys?: Set<string>
    };
  }

  export function addProperty(target: ITarget, key: string) {
    let keys = target.constructor.$$unsubscribeOnDestroyKeys;
    if (!keys) {
      setOnDestroyEpilogue(target.constructor.prototype, ngOnDestroyEpilogue);

      keys = target.constructor.$$unsubscribeOnDestroyKeys = new Set<string>();
    }

    keys.add(key);
  }

  function ngOnDestroyEpilogue(this: ITarget) {
    let keys = this.constructor.$$unsubscribeOnDestroyKeys;
    if (!keys?.size)
      return;

    // Grab copy of the keys, and clear immediately.
    // In case this were to run twice - we don't want to process the keys twice
    let keysCopy = Array.from(keys);
    keys.clear();

    for (let k of keysCopy) {
      tryUnsubscribe(this, k);
    }
  }

  function tryUnsubscribe(target: any, key: string) {
    try {
      let sub = target[key];
      if (sub == null || sub == undefined)
        return;

      if ('unsubscribe' in sub) {
        let closed = ('closed' in sub) && sub.closed === true;
        if (!closed) {
          sub.unsubscribe();

          LOGGER.debug(`Unsubscribed '${key}'.`, { target });
        }
      } else {
        LOGGER.warn(`'${key}' is not SubscriptionLike.`, { target, value: sub });
      }
    }
    catch (err) {
      LOGGER.error(`Failed to unsubscribe '${key}'`, { target, err });
    }
  }


  /** Overrides a function in the target, with a callback that will be called after the original function. */
  function setOnDestroyEpilogue(target: ITargetPrototype, epilogue: Function) {
    try {
      let original = target.ngOnDestroy;
      if (original) {
        target.ngOnDestroy = function () {
          try {
            original!.call(target, ...arguments);
          }
          finally {
            epilogue.call(target, ...arguments);
          }
        };
      }
      else {
        target.ngOnDestroy = epilogue;
      }

      LOGGER.debug(`Added ngOnDestroy callback.`, { target, original, epilogue });
    }
    catch (err) {
      LOGGER.error(`Could not add ngOnDestroy callback.`, { target, epilogue });
      throw err;
    }
  }
}
